import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';

const MLM_LOGO = "https://customer-assets.emergentagent.com/job_5389659f-dea1-4240-af6c-b14886122f6e/artifacts/6yszwf33_Digital%20Logo%20with%20Framed%20Letters.png";
const GIGFLEET_LOGO = "https://customer-assets.emergentagent.com/job_5389659f-dea1-4240-af6c-b14886122f6e/artifacts/plrdoiq9_Gig%20Fleet%20Logo.png";

const footerLinks = {
  movement: [
    { label: 'About Us', path: '/about' },
    { label: 'The Problem', path: '/problem' },
    { label: 'The Solution', path: '/solution' },
    { label: 'Our Impact', path: '/impact' },
  ],
  getInvolved: [
    { label: 'Corporate CSI', path: '/corporate-csi' },
    { label: 'For Riders', path: '/for-riders' },
    { label: 'Advertising', path: '/advertising' },
    { label: 'Contact Us', path: '/contact' },
  ],
  resources: [
    { label: 'Insights & News', path: '/insights' },
    { label: 'Privacy Policy', path: '/privacy' },
    { label: 'Terms of Service', path: '/terms' },
  ],
};

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      {/* Main Footer */}
      <div className="container-wide py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-2 space-y-6">
            <img
              src={MLM_LOGO}
              alt="#MyLifeMatters"
              className="h-12 w-auto brightness-0 invert"
            />
            <p className="text-slate-400 text-sm leading-relaxed max-w-sm">
              Creating a New Gig Economy and Social Compact for Gigpreneurs. 
              Building awareness around the lives, safety, and economic dignity 
              of delivery riders in South Africa.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3 pt-2">
              <a
                href="tel:+27720286584"
                className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors text-sm"
                data-testid="footer-phone"
              >
                <Phone className="w-4 h-4" />
                <span>072 028 6584</span>
              </a>
              <a
                href="mailto:ask@gigfleet.co.za"
                className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors text-sm"
                data-testid="footer-email"
              >
                <Mail className="w-4 h-4" />
                <span>ask@gigfleet.co.za</span>
              </a>
              <div className="flex items-center gap-3 text-slate-400 text-sm">
                <MapPin className="w-4 h-4" />
                <span>South Africa</span>
              </div>
            </div>
          </div>

          {/* The Movement */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">The Movement</h4>
            <ul className="space-y-3">
              {footerLinks.movement.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-white transition-colors text-sm"
                    data-testid={`footer-link-${link.path.slice(1)}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Get Involved */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">Get Involved</h4>
            <ul className="space-y-3">
              {footerLinks.getInvolved.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-white transition-colors text-sm"
                    data-testid={`footer-link-${link.path.slice(1)}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Strategic Partner */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">Strategic Partner</h4>
            <div className="bg-white/10 rounded-xl p-4 inline-block">
              <img
                src={GIGFLEET_LOGO}
                alt="Gig Fleet Management"
                className="h-10 w-auto"
              />
            </div>
            <p className="text-slate-400 text-xs mt-3 leading-relaxed">
              Powered by Gig Fleet Management — providing technology, training, 
              and operational systems for rider empowerment.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="container-wide py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm text-center md:text-left">
            © {currentYear} #MyLifeMatters. All rights reserved.
          </p>
          
          {/* Social Links Placeholders */}
          <div className="flex items-center gap-4">
            <a
              href="https://facebook.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full bg-slate-800 hover:bg-primary-mlm flex items-center justify-center transition-colors"
              aria-label="Facebook"
              data-testid="social-facebook"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a
              href="https://instagram.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full bg-slate-800 hover:bg-primary-mlm flex items-center justify-center transition-colors"
              aria-label="Instagram"
              data-testid="social-instagram"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
            </a>
            <a
              href="https://tiktok.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full bg-slate-800 hover:bg-primary-mlm flex items-center justify-center transition-colors"
              aria-label="TikTok"
              data-testid="social-tiktok"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
              </svg>
            </a>
            <a
              href="https://linkedin.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full bg-slate-800 hover:bg-primary-mlm flex items-center justify-center transition-colors"
              aria-label="LinkedIn"
              data-testid="social-linkedin"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
